import React from 'react'
import styled from 'styled-components'

import {Text, text} from '@/components/text'
import {InfoTooltip} from '@/components/tooltip/info-tooltip'
import checkIcon from '@/images/icons/check-pink.svg'
import mq from '@/utils/media-query'

interface Props {
    headColumns: string[]
    bodyHeads: string[]
    bodyHeadsTooltips?: string[]
    bodyColumns: Array<string | boolean>[]
    tickAltText?: string
    disclaimer?: string
}

export const Table = React.forwardRef<HTMLDivElement, Props>(
    (
        {headColumns, bodyHeads, bodyHeadsTooltips, bodyColumns, tickAltText, disclaimer, ...props},
        ref,
    ) => {
        return (
            <TableWrapper ref={ref} {...props}>
                <StyledTable>
                    <tbody>
                        <tr>
                            {headColumns.map((headColumn, index) => (
                                <HeadColumn
                                    scope="col"
                                    key={`head-${index}`}
                                    $numberOfColumns={headColumns.length}
                                >
                                    {headColumn}
                                </HeadColumn>
                            ))}
                        </tr>

                        {bodyColumns.map((bodyColumn, rowIndex) => (
                            <BodyRow key={`body-row-${rowIndex}`}>
                                <BodyHead scope="row">
                                    {bodyHeads[rowIndex]}
                                    {bodyHeadsTooltips?.length && bodyHeadsTooltips[rowIndex] && (
                                        <InfoTooltip label={bodyHeadsTooltips[rowIndex]} />
                                    )}
                                </BodyHead>
                                {bodyColumn.map((cell, index) => (
                                    <BodyColumn key={`body-cell-${rowIndex}-${index}`}>
                                        {typeof cell === 'string' && cell}
                                        {cell === true && <StyledTick aria-label={tickAltText} />}
                                    </BodyColumn>
                                ))}
                            </BodyRow>
                        ))}
                    </tbody>
                </StyledTable>

                {disclaimer && (
                    <StyledText $variant="paragraph-base" $color="lightMuted">
                        {disclaimer}
                    </StyledText>
                )}
            </TableWrapper>
        )
    },
)

const TableWrapper = styled.div`
    overflow-x: auto;
`

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th:first-child {
        text-align: left;
    }

    td {
        text-align: center;
    }
`

const BodyHead = styled.th`
    ${text({$variant: 'paragraph-base'})}

    ${mq.max('md')} {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: start;
    }
`

const BodyColumn = styled.td`
    ${text({$variant: 'label-base'})}
`

const BodyRow = styled.tr`
    th,
    td {
        padding: ${({theme}) => theme.spacings.s16} 0;
        font-weight: normal;
    }

    &:not(:last-child) {
        border-bottom: ${({theme}) => theme.borders.default};
    }

    &:last-child {
        th,
        td {
            padding-bottom: 0;
        }
    }
`

type HeadColumnProps = {
    $numberOfColumns: number
}

const HeadColumn = styled.th<HeadColumnProps>`
    min-width: 120px;
    padding-bottom: 20px;
    vertical-align: top;
    ${text({$variant: 'label-base-medium'})}

    &:not(:first-child) {
        width: ${(props) => ((2 / 3) * 100) / (props.$numberOfColumns - 1)}%;
    }

    ${mq.min('sm')} {
        ${text({$variant: 'paragraph-large'})}
    }
`

const StyledTick = styled.span`
    display: inline-block;
    width: 16px;
    aspect-ratio: 4 / 3;
    background-image: url(${checkIcon});
    background-size: contain;

    ${mq.min('sm')} {
        width: 20px;
    }
`

const StyledText = styled(Text)`
    margin-top: ${({theme}) => theme.spacings.s20};

    ${mq.min('lg')} {
        margin-top: ${({theme}) => theme.spacings.s40};
    }
`
